import { SofaModule } from './SofaModule';
import { CSS2DObject } from 'three/examples/jsm/renderers/CSS2DRenderer.js';
import { ProjectRenderer } from '../ProjectRenderer';
import { UUID } from '@/types/types';
import { Loader } from '../Loader';
import { Object3DJSON, Object3D, Vector3 } from 'three';
import { UserDataProp } from '../types';
import PlusIcon from '@/assets/icons/plus.svg?raw';

export interface ConnectingSlotData {
	id: UUID;
	slotType: SlotType;
	slotSide: SlotSide;
	point: Object3DJSON;
	connectedSlot: UUID | null;
}

export enum SlotSide {
	LEFT = 'left',
	RIGHT = 'right',
	BOTTOM = 'bottom',
	TOP = 'top',
}

export enum SlotType {
	LEFT = 'left',
	RIGHT = 'right',
	UNIVERSAL = 'universal',
	HOST = 'host',
	ACCESSORY = 'accessory',
}

export class ConnectingSlot {
	id = crypto.randomUUID();
	hostModule: SofaModule;
	slotType: SlotType;
	slotSide: SlotSide;
	point = new Object3D();
	connectedSlot: ConnectingSlot | null = null;
	private loader = Loader.getInstance();
	private renderer: ProjectRenderer;
	private button = document.createElement('button');
	private css2dObject?: CSS2DObject;

	constructor(
		renderer: ProjectRenderer,
		hostModule: SofaModule,
		slotType: SlotType,
		object?: Object3D
	) {
		this.renderer = renderer;

		this.hostModule = hostModule;
		this.slotType = slotType;

		if (object) {
			this.initialize(object);
			const slotSide = object.userData[UserDataProp.SLOT_SIDE];

			this.slotSide = slotSide || this.slotType;
		} else {
			this.slotSide = this.slotType as unknown as SlotSide;
		}
	}

	private initialize(object: Object3D) {
		this.setupObject(object);
		this.initializeAddModuleButton();
		this.setVisibility();
		this.addEventListeners();
	}

	private setupObject(object: Object3D) {
		this.point.copy(object);
		this.hostModule.group.add(this.point);
	}

	private initializeAddModuleButton() {
		const displayObj = this.point.children[0];
		if (!displayObj) throw new Error('MISSING_DISPLAY_OBJECT');

		this.button.className = 'add-module-button';
		this.button.innerHTML = PlusIcon;
		this.button.type = 'button';

		const worldPosition = new Vector3();
		displayObj.getWorldPosition(worldPosition);

		this.hostModule.group.worldToLocal(worldPosition);

		this.css2dObject = new CSS2DObject(this.button);
		this.css2dObject.position.copy(worldPosition);

		this.hostModule.group.add(this.css2dObject);
		this.css2dObject.layers.set(0);
	}

	private addEventListeners() {
		this.button.addEventListener('click', () => {
			this.renderer.selectedConnectingSlot = this;
		});
	}

	async setFromData(data: ConnectingSlotData) {
		const object = await this.loader.objectLoader.parseAsync(data.point);
		this.id = data.id;
		this.slotSide = data.slotSide;
		this.initialize(object);
	}

	setVisibility() {
		if (!this.css2dObject) return;

		if (this.renderer.isPreviewMode) {
			this.css2dObject.visible = false;
			return;
		}

		let visible = true;
		if (this.renderer.selectedConnectingSlot)
			visible = this.renderer.selectedConnectingSlot === this;
		if (this.connectedSlot) visible = false;

		this.css2dObject.visible = visible;
	}

	canConnectTo(slot: ConnectingSlot) {
		if (this.connectedSlot || slot.connectedSlot) return false;
		if (this.slotType === SlotType.UNIVERSAL) return true;

		switch (slot.slotType) {
			case SlotType.LEFT:
				return this.slotType === SlotType.RIGHT;
			case SlotType.RIGHT:
				return this.slotType === SlotType.LEFT;
			case SlotType.UNIVERSAL:
				return true;
			case SlotType.HOST:
				return this.slotType === SlotType.ACCESSORY;
			case SlotType.ACCESSORY:
				return this.slotType === SlotType.HOST;

			default:
				return false;
		}
	}

	connectTo(slot: ConnectingSlot) {
		if (!this.canConnectTo(slot)) return;

		this.connectedSlot = slot;
		slot.connectedSlot = this;

		this.setVisibility();
		slot.setVisibility();
	}

	disconnectSlots(slot: ConnectingSlot) {
		if (!this.connectedSlot) return;

		this.connectedSlot = null;
		slot.disconnectSlots(this);
	}

	dispose() {
		this.css2dObject?.removeFromParent();
		this.button.remove();

		if (this.connectedSlot) this.connectedSlot.connectedSlot = null;
		this.connectedSlot = null;
		this.point.removeFromParent();
	}

	export(): ConnectingSlotData {
		return {
			id: this.id,
			slotType: this.slotType,
			slotSide: this.slotSide,
			point: this.point.toJSON(),
			connectedSlot: this.connectedSlot?.id ?? null,
		};
	}
}
